import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const AudioPlayerContext = createContext(null);

const normalizeMediaUrl = (url) => {
  if (!url) return url;
  let u = url;
  if (u.includes("localhost:443/")) {
    u = u.replace("localhost:443/", "localhost:8080/");
  }
  try {
    const loc = window.location;
    const isHttpsPage = loc.protocol === "https:";
    const urlObj = new URL(u, loc.origin);
    if (u.startsWith("/public/")) {
      urlObj.protocol = loc.protocol;
      urlObj.host = loc.host;
    }
    if (isHttpsPage && urlObj.protocol === "http:") {
      urlObj.protocol = "https:";
    }
    return urlObj.toString();
  } catch {
    return u;
  }
};

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(() => parseFloat(localStorage.getItem("audioPlaybackRate")) || 1);
  const [volume, setVolume] = useState(() => parseFloat(localStorage.getItem("audioVolume")) || 1);

  const currentTrack = index >= 0 && index < queue.length ? queue[index] : null;

  const indexRef = useRef(index);
  const queueRef = useRef(queue);
  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { queueRef.current = queue; }, [queue]);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "metadata";
      audioRef.current.crossOrigin = "anonymous";
    }
    const audio = audioRef.current;

    const onLoadedMetadata = () => setDuration(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      const i = indexRef.current || 0;
      const q = queueRef.current || [];
      if (i + 1 < q.length) {
        playAt(i + 1);
      } else {
        stop();
      }
    };
    const onError = (e) => {
      console.error("Audio error:", e);
      // Tenta pular para o próximo em caso de erro
      const i = indexRef.current || 0;
      const q = queueRef.current || [];
      if (i + 1 < q.length) playAt(i + 1); else stop();
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    audio.playbackRate = playbackRate;
    audio.volume = volume;

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrack = useCallback((trk, startTime = 0) => {
    const audio = audioRef.current;
    if (!audio || !trk) return;
    const src = normalizeMediaUrl(trk.url);
    // iOS/Safari preferem mp3
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    audio.src = (isIOS || isSafari) ? src.replace(".ogg", ".mp3") : src;
    audio.currentTime = startTime || 0;
  }, []);

  const playAt = useCallback((idx, startTime = 0) => {
    const q = queueRef.current;
    if (!q || idx < 0 || idx >= q.length) return;
    setIndex(idx);
    const trk = q[idx];
    const audio = audioRef.current;
    loadTrack(trk, startTime);
    // Tenta tocar; se falhar por metadata, tenta novamente no loadedmetadata
    const tryPlay = () => audio.play().catch(() => {});
    if (isNaN(audio.duration) || audio.readyState < 2) {
      const once = () => { audio.removeEventListener('loadedmetadata', once); tryPlay(); };
      audio.addEventListener('loadedmetadata', once);
      try { audio.load(); } catch {}
    } else {
      tryPlay();
    }
  }, [loadTrack]);

  const playQueue = useCallback((tracks, startIndex = 0, startTime = 0) => {
    if (!Array.isArray(tracks) || !tracks.length) return;
    const normalized = tracks.map(t => ({ ...t, url: normalizeMediaUrl(t.url) }));
    setQueue(normalized);
    setIndex(startIndex);
    // Aguarda o estado ser aplicado antes de tocar
    setTimeout(() => playAt(startIndex, startTime), 0);
  }, [playAt]);

  const play = useCallback(() => {
    if (!currentTrack) return;
    audioRef.current.play().catch(() => {});
  }, [currentTrack]);

  const pause = useCallback(() => {
    audioRef.current.pause();
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIndex(-1);
    setQueue([]);
  }, []);

  const next = useCallback(() => {
    if (index + 1 < queue.length) {
      playAt(index + 1, 0);
    } else {
      stop();
    }
  }, [index, queue.length, playAt, stop]);

  const prev = useCallback(() => {
    if (index - 1 >= 0) {
      playAt(index - 1, 0);
    } else {
      audioRef.current.currentTime = 0;
    }
  }, [index, playAt]);

  const seek = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(time, duration || audio.duration || 0));
  }, [duration]);

  const setRate = useCallback((rate) => {
    const r = parseFloat(rate) || 1;
    setPlaybackRate(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
    localStorage.setItem("audioPlaybackRate", String(r));
  }, []);

  const setVol = useCallback((vol) => {
    const v = Math.max(0, Math.min(1, parseFloat(vol) || 1));
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    localStorage.setItem("audioVolume", String(v));
  }, []);

  const handoffFromElement = useCallback((audioEl, tracks, startIndex) => {
    try {
      const startTime = audioEl?.currentTime || 0;
      playQueue(tracks, startIndex, startTime);
    } catch (e) {
      console.error("Handoff falhou:", e);
    }
  }, [playQueue]);

  const value = useMemo(() => ({
    // estado
    queue, index, currentTrack, isPlaying, currentTime, duration, playbackRate, volume,
    // ações
    playQueue, play, pause, stop, next, prev, seek, setRate, setVol, handoffFromElement,
  }), [queue, index, currentTrack, isPlaying, currentTime, duration, playbackRate, volume, playQueue, play, pause, stop, next, prev, seek, setRate, setVol, handoffFromElement]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayer = () => useContext(AudioPlayerContext);

export default AudioPlayerContext;
